class QuickAddModal {
  constructor() {
    this.modal = document.getElementById("quick-add-modal");
    this.modalContent = document.getElementById("quick-add-modal-content");
    this.closeButton = document.querySelector(".quick-add-modal__close");
    this.currentProductHandle = null;
    this.currentProductData = null;
    this.currentZoomIndex = 0; // Track current zoomed image index

    if (!this.modal || !this.modalContent) return;

    this.initEvents();
  }

  initEvents() {
    if (this.closeButton) {
      this.closeButton.addEventListener("click", () => this.close());
    }
    this.modal.addEventListener("click", (e) => {
      if (e.target === this.modal) this.close();
    });
    document.addEventListener("keydown", (e) => {
      if (e.key === "Escape") this.close();
    });

    // Handle quick-add buttons - both direct add and modal
    document.addEventListener("click", (e) => {
      const quickAddBtn = e.target.closest(".quick-add-btn");
      if (!quickAddBtn) return;

      e.preventDefault();
      const productHandle = quickAddBtn.dataset.productHandle;
      const hasVariants = quickAddBtn.dataset.hasVariants === "true";
      const variantId = quickAddBtn.dataset.variantId;

      if (!productHandle) return;

      // Direct add to cart for single variant products
      if (!hasVariants && variantId) {
        this.addToCartDirect(variantId, quickAddBtn);
      } else {
        // Open modal for products with variants
        this.open(productHandle);
      }
    });
  }

  open(productHandle) {
    if (!this.modal || !this.modalContent) return;

    if (
      this.currentProductHandle === productHandle &&
      this.modal.style.display === "flex"
    ) {
      return;
    }

    this.currentProductHandle = productHandle;
    this.showLoading();
    this.modal.style.display = "flex";
    document.body.style.overflow = "hidden";

    this.fetchProduct(productHandle);
  }

  close() {
    if (!this.modal) return;
    this.modal.style.display = "none";
    document.body.style.overflow = "";
    this.currentProductHandle = null;
    this.currentProductData = null;
    if (this.modalContent) this.modalContent.innerHTML = "";
  }

  async addToCartDirect(variantId, button) {
    const originalText =
      button.querySelector(".quick-add-btn-label")?.textContent ||
      button.textContent;
    const labelElement = button.querySelector(".quick-add-btn-label");

    // Disable button and show loading state
    button.disabled = true;
    if (labelElement) {
      labelElement.textContent = "Adding...";
    } else {
      button.textContent = "Adding...";
    }

    try {
      const cartUrl = window.routes.cart_add_url + ".js";
      const response = await fetch(cartUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          items: [
            {
              id: variantId,
              quantity: 1,
            },
          ],
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Publish cart update event
        publish(PUB_SUB_EVENTS.cartUpdate, {
          action: "add",
          variant: data,
          productId: button.dataset.productId,
        });

        // Show success state
        if (labelElement) {
          labelElement.textContent = "Added!";
        } else {
          button.textContent = "Added!";
        }

        // Reset after delay
        setTimeout(() => {
          button.disabled = false;
          if (labelElement) {
            labelElement.textContent = originalText;
          } else {
            button.textContent = originalText;
          }
        }, 1500);
      } else {
        throw new Error(data.description || "Failed to add to cart");
      }
    } catch (error) {
      console.error("Error adding to cart:", error);

      // Publish error event
      publish(PUB_SUB_EVENTS.cartError, {
        error: error.message,
        context: "quick-add-direct",
        productId: button.dataset.productId,
      });

      if (labelElement) {
        labelElement.textContent = "Error - Try Again";
      } else {
        button.textContent = "Error - Try Again";
      }

      setTimeout(() => {
        button.disabled = false;
        if (labelElement) {
          labelElement.textContent = originalText;
        } else {
          button.textContent = originalText;
        }
      }, 2000);
    }
  }

  showLoading() {
    this.modalContent.innerHTML = `
      <div class="quick-add-loading">
        <p class="loading-text">Loading product</p>
        <div class="loading-dots">
          <span class="dot"></span>
          <span class="dot"></span>
          <span class="dot"></span>
        </div>
      </div>
    `;
  }

  showError() {
    this.modalContent.innerHTML = `
      <div class="quick-add-error">
        <p>Failed to load product. Please try again.</p>
        <button type="button" onclick="window.quickAddModal.close()">Close</button>
      </div>
    `;
  }

  async fetchProduct(productHandle) {
    if (!this.modalContent) return;

    const SECTION_ID = "product-quick-add";

    try {
      const localeRoot = (window.Shopify?.routes?.root || "/").replace(
        /\/?$/,
        "/"
      );
      const productPath = `${localeRoot}products/${encodeURIComponent(
        productHandle
      )}`;
      const fetchOpts = { credentials: "same-origin" };

      const loadSectionHtml = async () => {
        const sectionsUrl = `${productPath}?sections=${encodeURIComponent(
          SECTION_ID
        )}`;
        const jsonRes = await fetch(sectionsUrl, fetchOpts);

        if (jsonRes.ok) {
          const rawText = await jsonRes.text();
          if (rawText.trimStart().startsWith("{")) {
            try {
              const data = JSON.parse(rawText);
              let html =
                data[SECTION_ID] ?? data.sections?.[SECTION_ID];
              if (!html && typeof data === "object" && data !== null) {
                const vals = Object.values(data).filter(
                  (v) =>
                    typeof v === "string" &&
                    (v.includes("quick-add-product") ||
                      v.includes("shopify-section"))
                );
                html = vals.find((s) => s.includes("quick-add-product"));
              }
              if (html && typeof html === "string") return html;
            } catch (_) {
              /* fall through to section_id */
            }
          }
        }

        const sidUrl = `${productPath}?section_id=${encodeURIComponent(
          SECTION_ID
        )}`;
        const htmlRes = await fetch(sidUrl, fetchOpts);
        if (!htmlRes.ok) throw new Error(`Fetch failed: ${htmlRes.status}`);
        return htmlRes.text();
      };

      const text = await loadSectionHtml();
      const parser = new DOMParser();
      const doc = parser.parseFromString(text, "text/html");

      let productHTML = doc.querySelector(".quick-add-product");
      if (!productHTML) {
        doc.querySelectorAll(".shopify-section").forEach((wrap) => {
          const inner = wrap.querySelector(".quick-add-product");
          if (inner && !productHTML) productHTML = inner;
        });
      }

      if (!productHTML) {
        console.error(
          "Quick add: .quick-add-product not found",
          text.slice(0, 1200)
        );
        throw new Error("Invalid product content");
      }

      const imported = document.importNode(productHTML, true);
      this.modalContent.innerHTML = "";
      this.modalContent.appendChild(imported);

      const variantsData = imported.dataset.productVariants;
      if (variantsData) {
        this.currentProductData = JSON.parse(variantsData);
      }

      this.initProductScripts();
    } catch (error) {
      console.error("Quick add error:", error);
      this.showError();
    }
  }

  initProductScripts() {
    const productContainer =
      this.modalContent.querySelector(".quick-add-product");
    if (!productContainer) return;

    const productHandle = productContainer.dataset.productHandle;
    if (!productHandle) return;

    this.initImageSlider(productContainer);
    this.initImageZoom(productContainer);
    this.initDescriptionTruncate(productContainer);
    this.syncVariantLegendLabels(productContainer);
    this.initVariantSelection(productContainer, productHandle);
    this.initBundleSelection(productContainer);
    this.initBundleListSelection(productContainer);
    this.initQuantitySelector(productContainer);
    this.initFormSubmission(productContainer);
  }

  initBundleListSelection(container) {
    const root = container.querySelector('[data-bundle-list]');
    if (!root) return;
    if (typeof window.initBundleList === 'function') {
      window.initBundleList({
        root: root,
        priceScope: container,
        useFallbackAccordion: true,
      });
    }
  }

  initBundleSelection(container) {
    const bundleRadios = container.querySelectorAll(
      'input[name^="bundle-selection-"]'
    );
    if (!bundleRadios.length) return;
    const variantInput = container.querySelector('input[name="id"]');
    if (!variantInput) return;
    bundleRadios.forEach((radio) => {
      radio.addEventListener("change", () => {
        if (radio.checked) variantInput.value = radio.value;
      });
    });
  }

  initDescriptionTruncate(container) {
    const descriptionEl = container.querySelector(
      ".product-description[data-truncate-words]"
    );
    if (!descriptionEl) return;

    const truncateWords = parseInt(descriptionEl.dataset.truncateWords, 10) || 50;
    const contentEl = descriptionEl.querySelector(".description-content");
    const readMoreBtn = descriptionEl.querySelector(".read-more-btn");
    const showLessBtn = descriptionEl.querySelector(".show-less-btn");

    if (!contentEl) return;

    const fullText = contentEl.textContent.trim();
    const words = fullText.split(/\s+/).filter(Boolean);

    if (words.length <= truncateWords) return;

    const truncatedText = words.slice(0, truncateWords).join(" ") + "...";
    contentEl.dataset.fullText = fullText;
    contentEl.textContent = truncatedText;
    contentEl.dataset.isTruncated = "true";

    if (readMoreBtn) {
      readMoreBtn.style.display = "inline-block";
      readMoreBtn.addEventListener("click", () => {
        contentEl.textContent = contentEl.dataset.fullText;
        contentEl.dataset.isTruncated = "false";
        readMoreBtn.style.display = "none";
        if (showLessBtn) showLessBtn.style.display = "inline-block";
      });
    }

    if (showLessBtn) {
      showLessBtn.addEventListener("click", () => {
        contentEl.textContent = truncatedText;
        contentEl.dataset.isTruncated = "true";
        showLessBtn.style.display = "none";
        if (readMoreBtn) readMoreBtn.style.display = "inline-block";
      });
    }
  }

  syncVariantLegendLabels(container) {
    container.querySelectorAll(".variant-option").forEach((group) => {
      const checked = group.querySelector('input[type="radio"]:checked');
      const sel = group.querySelector("[data-selected-value]");
      if (checked && sel) sel.textContent = checked.value;
    });
  }

  syncHiddenVariantSelect(container, variantId) {
    const select = container.querySelector('select.product-form__variants');
    if (!select || variantId == null) return;
    select.value = String(variantId);
  }

  initImageSlider(container) {
    const slider = container.querySelector(".quick-add-slides");
    const slides = container.querySelectorAll(".quick-add-slide");
    const prevBtn = container.querySelector(".quick-add-prev");
    const nextBtn = container.querySelector(".quick-add-next");
    const counter = container.querySelector(
      ".quick-add-counter .current-slide"
    );

    if (!slider || slides.length === 0) return;

    let currentSlide = 0;
    const slideCount = slides.length;

    const updateCounter = () => {
      if (counter) counter.textContent = currentSlide + 1;
    };

    const goToSlide = (index) => {
      currentSlide = (index + slideCount) % slideCount;
      slider.style.transform = `translateX(-${currentSlide * 100}%)`;
      updateCounter();
    };

    const nextSlide = () => goToSlide(currentSlide + 1);
    const prevSlide = () => goToSlide(currentSlide - 1);

    if (nextBtn) nextBtn.addEventListener("click", nextSlide);
    if (prevBtn) prevBtn.addEventListener("click", prevSlide);

    // Touch events for swipe
    let touchStartX = 0;
    let touchEndX = 0;

    slider.addEventListener(
      "touchstart",
      (e) => {
        touchStartX = e.changedTouches[0].screenX;
      },
      { passive: true }
    );

    slider.addEventListener(
      "touchend",
      (e) => {
        touchEndX = e.changedTouches[0].screenX;
        if (touchEndX < touchStartX - 50) nextSlide();
        if (touchEndX > touchStartX + 50) prevSlide();
      },
      { passive: true }
    );

    // Keyboard navigation
    document.addEventListener("keydown", (e) => {
      if (e.key === "ArrowRight") nextSlide();
      if (e.key === "ArrowLeft") prevSlide();
    });

    updateCounter();
  }

  // Add these new methods for zoom functionality
  initImageZoom(container) {
    const zoomPopup = container.querySelector(".quick-image-zoom-popup");
    const zoomImages = container.querySelectorAll(".quick-zoom-image");
    const zoomImageContainer = container.querySelector(
      ".quick-zoom-image-container"
    );
    const zoomCloseBtn = container.querySelector(".quick-zoom-close-btn");
    const zoomPrevBtn = container.querySelector(".quick-zoom-nav.prev");
    const zoomNextBtn = container.querySelector(".quick-zoom-nav.next");
    const zoomCurrentSlide = container.querySelector(
      ".quick-zoom-counter .quick-current-slide"
    );
    const zoomTotalSlides = container.querySelector(
      ".quick-zoom-counter .quick-total-slides"
    );
    const mainZoomBtn = container.querySelector(".quick-main_zoom");

    if (!zoomPopup) return;

    // Initialize zoom popup
    const initZoomPopup = () => {
      zoomTotalSlides.textContent = zoomImages.length;

      // Open zoom popup
      mainZoomBtn.addEventListener("click", () => {
        const currentSlide = container.querySelector(
          ".quick-add-slide[data-index]"
        );
        this.currentZoomIndex = parseInt(currentSlide.dataset.index || "0");
        updateZoomPopup();
        zoomPopup.classList.add("active");
        document.body.style.overflow = "hidden";
      });

      // Close zoom popup
      zoomCloseBtn.addEventListener("click", () => {
        zoomPopup.classList.remove("active");
        document.body.style.overflow = "hidden"; // Keep hidden because modal is still open
      });

      // Close when clicking outside image
      zoomPopup.addEventListener("click", (e) => {
        if (e.target === zoomPopup) {
          zoomPopup.classList.remove("active");
          document.body.style.overflow = "hidden";
        }
      });

      // Keyboard navigation
      document.addEventListener("keydown", (e) => {
        if (zoomPopup.classList.contains("active")) {
          if (e.key === "Escape") {
            zoomPopup.classList.remove("active");
            document.body.style.overflow = "hidden";
          }
          if (e.key === "ArrowRight") nextZoomImage();
          if (e.key === "ArrowLeft") prevZoomImage();
        }
      });

      // Navigation buttons
      zoomPrevBtn?.addEventListener("click", (e) => {
        e.stopPropagation();
        prevZoomImage();
      });

      zoomNextBtn?.addEventListener("click", (e) => {
        e.stopPropagation();
        nextZoomImage();
      });

      // Swipe gestures for mobile
      let touchStartX = 0;
      let touchEndX = 0;

      zoomImageContainer.addEventListener(
        "touchstart",
        (e) => {
          touchStartX = e.changedTouches[0].screenX;
        },
        { passive: true }
      );

      zoomImageContainer.addEventListener(
        "touchend",
        (e) => {
          touchEndX = e.changedTouches[0].screenX;
          handleZoomSwipe();
        },
        { passive: true }
      );

      const handleZoomSwipe = () => {
        if (touchEndX < touchStartX - 50) nextZoomImage();
        if (touchEndX > touchStartX + 50) prevZoomImage();
      };

      const nextZoomImage = () => {
        this.currentZoomIndex = (this.currentZoomIndex + 1) % zoomImages.length;
        updateZoomPopup();
      };

      const prevZoomImage = () => {
        this.currentZoomIndex =
          (this.currentZoomIndex - 1 + zoomImages.length) % zoomImages.length;
        updateZoomPopup();
      };

      const updateZoomPopup = () => {
        zoomImages.forEach((img, index) => {
          img.classList.toggle("active", index === this.currentZoomIndex);
        });
        if (zoomCurrentSlide) {
          zoomCurrentSlide.textContent = this.currentZoomIndex + 1;
        }
      };
    };

    initZoomPopup.call(this); // Call with this context
  }

  initVariantSelection(container, productHandle) {
    const variantRadios = container.querySelectorAll(
      '.variant-option input[type="radio"]'
    );
    const addToCartBtn = container.querySelector(".add-to-cart-btn");
    const variantInput = container.querySelector('input[name="id"]');
    const currentPriceEl = container.querySelector(".current-price");
    const comparePriceEl = container.querySelector(".compare-price");
    const stockDataElement = container.querySelector(
      "#quick-add-variant-stock-data"
    );
    const stockData = stockDataElement
      ? JSON.parse(stockDataElement.textContent)
      : null;
    const variantStockEls = container.querySelectorAll(
      ".variant-option .option-value"
    );

    // Get variant count for button text formatting
    const shopifyVariantCount =
      this.currentProductData && Array.isArray(this.currentProductData)
        ? this.currentProductData.length
        : 0;

    if (variantRadios.length === 0) return;

    // Initialize stock indicators
    if (stockData) {
      this.updateStockIndicators(container, stockData, variantStockEls, []);
    }

    // Set initial button text based on current variant
    if (
      addToCartBtn &&
      this.currentProductData &&
      this.currentProductData.length > 0
    ) {
      const currentVariantId = variantInput
        ? parseInt(variantInput.value)
        : null;
      if (currentVariantId) {
        const currentVariant = this.currentProductData.find(
          (v) => v.id === currentVariantId
        );
        if (currentVariant) {
          if (currentVariant.available) {
            let variantText = currentVariant.options
              ? currentVariant.options.join("/")
              : "";
            const addToCartText = "Add to cart";

            if (shopifyVariantCount > 1 && variantText) {
              addToCartBtn.textContent = `${addToCartText} - ${variantText}`;
            } else {
              addToCartBtn.textContent = addToCartText;
            }
            addToCartBtn.disabled = false;
          } else {
            addToCartBtn.disabled = true;
            addToCartBtn.textContent = "Sold out";
          }
        }
      }
    }

    variantRadios.forEach((radio) => {
      radio.addEventListener("change", () => {
        const selectedOptions = this.getSelectedOptions(container);
        const selectedVariant = this.findVariantByOptions(selectedOptions);

        if (selectedVariant) {
          if (variantInput) variantInput.value = selectedVariant.id;
          this.syncHiddenVariantSelect(container, selectedVariant.id);
          this.syncVariantLegendLabels(container);

          // Sync the "current product" bundle radio (auto-select it)
          const currentBundleRadio = container.querySelector(
            "[data-bundle-current]"
          );
          if (currentBundleRadio) {
            currentBundleRadio.value = selectedVariant.id;
            currentBundleRadio.checked = true;
          }

          // Update price display (matching main-product format)
          if (currentPriceEl) {
            currentPriceEl.textContent = Shopify.formatMoney(
              selectedVariant.price,
              window.money_without_trailing_zeros_format || window.money_format
            );
          }

          if (
            selectedVariant.compare_at_price &&
            selectedVariant.compare_at_price > selectedVariant.price
          ) {
            if (comparePriceEl) {
              comparePriceEl.textContent = Shopify.formatMoney(
                selectedVariant.compare_at_price,
                window.money_without_trailing_zeros_format ||
                  window.money_format
              );
              comparePriceEl.style.display = "inline";
            }
          } else {
            if (comparePriceEl) comparePriceEl.style.display = "none";
          }

          // Update button (matching main-product format)
          if (addToCartBtn) {
            if (selectedVariant.available) {
              let variantText = selectedVariant.options
                ? selectedVariant.options.join("/")
                : "";
              const addToCartText = "Add to cart";

              if (shopifyVariantCount > 1 && variantText) {
                addToCartBtn.textContent = `${addToCartText} - ${variantText}`;
              } else {
                addToCartBtn.textContent = addToCartText;
              }
              addToCartBtn.disabled = false;
            } else {
              addToCartBtn.disabled = true;
              addToCartBtn.textContent = "Sold out";
            }
          }

          // Update stock indicators
          if (stockData) {
            this.updateStockIndicators(
              container,
              stockData,
              variantStockEls,
              selectedOptions
            );
          }

          // Update image if variant has featured image
          if (selectedVariant.featured_image) {
            const newImageSrc =
              selectedVariant.featured_image.src.split("?")[0] + "?width=1000";
            const mainImage =
              container.querySelector(".main-image.variant-dynamic-image") ||
              container.querySelector(".quick-main-image");

            if (mainImage) {
              mainImage.src = newImageSrc;
              mainImage.alt = selectedVariant.featured_image.alt || "";
              mainImage.dataset.variantImage = selectedVariant.id;
            }
          }
        }
      });
    });
  }

  getSelectedOptions(container) {
    const selectedOptions = [];
    container.querySelectorAll(".variant-option").forEach((group) => {
      const checked = group.querySelector('input[type="radio"]:checked');
      const labels = group.querySelectorAll(".option-value");
      labels.forEach((label) => label.classList.remove("selected"));
      if (checked) {
        selectedOptions.push(checked.value);
        const checkedLabel = group.querySelector(`label[for="${checked.id}"]`);
        if (checkedLabel) checkedLabel.classList.add("selected");
      } else {
        selectedOptions.push(null);
      }
    });
    return selectedOptions;
  }

  findVariantByOptions(options) {
    if (!this.currentProductData) return null;
    return this.currentProductData.find((variant) => {
      return variant.options.every(
        (opt, idx) => !options[idx] || options[idx] === opt
      );
    });
  }

  updateStockIndicators(
    container,
    stockData,
    variantStockEls,
    selectedOptions = []
  ) {
    if (!stockData) return;
    const optionsCount = stockData.options.length;

    variantStockEls.forEach((el) => {
      const labelValue = el.getAttribute("title")?.trim();
      const fieldset = el.closest(".variant-option");
      const optionIndex = Array.from(fieldset.parentNode.children).indexOf(
        fieldset
      );

      // Remove old stock indicator
      el.querySelector(".variant-stock")?.remove();

      let stockLeft = 0;

      if (optionsCount === 1) {
        // Single option → sum directly
        stockLeft = stockData.variants
          .filter((v) => v.options[0] === labelValue)
          .reduce((acc, v) => acc + v.stock, 0);
      } else if (optionsCount >= 2) {
        if (optionIndex === 0) {
          // First-level (e.g., Size)
          stockLeft = stockData.variants
            .filter((v) => v.options[0] === labelValue)
            .reduce((acc, v) => acc + v.stock, 0);
        } else if (optionIndex === 1 && selectedOptions[0]) {
          // Second-level (e.g., Age) → based on selected Size
          stockLeft = stockData.variants
            .filter(
              (v) =>
                v.options[0] === selectedOptions[0] &&
                v.options[1] === labelValue
            )
            .reduce((acc, v) => acc + v.stock, 0);
        }
      }

      if (stockLeft > 0 && stockLeft < 10) {
        const urgent = stockLeft < 5;
        const isSwatch =
          el.hasAttribute("style") &&
          el.getAttribute("style").includes("--swatch");
        const div = document.createElement("div");
        div.className = `variant-stock ${
          isSwatch ? "variant-stock--swatch" : ""
        }`;
        div.dataset.stock = stockLeft;
        div.dataset.urgent = urgent ? "true" : "false";
        div.innerHTML = `
          <div class="dot ${urgent ? "urgent" : "warning"}"></div>
          ${stockLeft} left
        `;
        el.appendChild(div);
      }
    });
  }

  initQuantitySelector(container) {
    const minusBtn = container.querySelector(".quantity-btn.minus");
    const plusBtn = container.querySelector(".quantity-btn.plus");
    const quantityInput = container.querySelector(".quantity-input");
    const stockError = container.querySelector(".stock-error");
    const productContainer = container.closest(".quick-add-product");

    if (!minusBtn || !plusBtn || !quantityInput) return;

    const getMaxQuantity = () => {
      const variantInput = container.querySelector('input[name="id"]');
      if (!variantInput) return 9999;

      const variantId = parseInt(variantInput.value);
      const variant = this.currentProductData?.find((v) => v.id === variantId);

      if (!variant) return 9999;

      const inventoryPolicy = variant.inventory_policy;
      const inventoryManagement = variant.inventory_management;

      if (inventoryManagement === "shopify" && inventoryPolicy === "deny") {
        return variant.inventory_quantity || 9999;
      }
      return 9999; // No limit if not tracking inventory
    };

    const getMinQuantity = () => {
      const minAttr = quantityInput.getAttribute("data-min");
      return minAttr ? parseInt(minAttr) : 1;
    };

    minusBtn.addEventListener("click", () => {
      const currentValue = parseInt(quantityInput.value);
      const minValue = getMinQuantity();
      if (currentValue > minValue) {
        quantityInput.value = currentValue - 1;
        if (stockError) stockError.textContent = ""; // Clear error when decreasing
      }
    });

    plusBtn.addEventListener("click", () => {
      const currentValue = parseInt(quantityInput.value);
      const maxQuantity = getMaxQuantity();

      if (currentValue >= maxQuantity) {
        if (stockError)
          stockError.textContent = `Only ${maxQuantity} available in stock`;
        return;
      }

      quantityInput.value = currentValue + 1;
      if (stockError) stockError.textContent = ""; // Clear error when increasing within limits
    });

    quantityInput.addEventListener("change", () => {
      const maxQuantity = getMaxQuantity();
      const minValue = getMinQuantity();
      let newValue = parseInt(quantityInput.value) || minValue;

      if (newValue < minValue) newValue = minValue;
      if (newValue > maxQuantity) {
        newValue = maxQuantity;
        if (stockError)
          stockError.textContent = `Only ${maxQuantity} available in stock`;
      } else {
        if (stockError) stockError.textContent = "";
      }

      quantityInput.value = newValue;
    });
  }

  initFormSubmission(container) {
    const form = container.querySelector(".quick-add-form");
    if (!form) return;

    form.addEventListener("submit", async (e) => {
      e.preventDefault();
      e.stopImmediatePropagation();

      const submitBtn = form.querySelector(".add-to-cart-btn");
      const originalText = submitBtn ? submitBtn.textContent : "";
      const quantityInput = form.querySelector(".quantity-input");
      const stockError = form.querySelector(".stock-error");
      const bundleRoot = container.querySelector("[data-bundle-list]");
      const isBundleMode =
        !!bundleRoot && typeof window.bundleListGetItems === "function";

      let payloadItems;

      if (isBundleMode) {
        // Bundle mode: skip parent-product validation entirely — we never add the parent.
        const bundleItems = window.bundleListGetItems(bundleRoot);
        if (bundleItems.length === 0) {
          if (stockError) {
            stockError.textContent =
              "Please select a product from each list.";
          }
          return;
        }
        payloadItems = bundleItems.map(function (it) {
          return { id: parseInt(it.id, 10) || it.id, quantity: it.quantity };
        });
      } else {
        const variantInput = form.querySelector('input[name="id"]');
        if (!variantInput) return;

        const variantId = parseInt(variantInput.value);
        const variant = this.currentProductData?.find(
          (v) => v.id === variantId
        );

        const quantity = quantityInput ? parseInt(quantityInput.value) : 1;
        const maxQuantity = variant?.inventory_quantity || 9999;
        const inventoryPolicy = variant?.inventory_policy;
        const inventoryManagement = variant?.inventory_management;

        if (
          inventoryManagement === "shopify" &&
          inventoryPolicy === "deny" &&
          quantity > maxQuantity
        ) {
          if (stockError)
            stockError.textContent = `Only ${maxQuantity} available in stock`;
          if (quantityInput) quantityInput.value = maxQuantity;
          return;
        }

        payloadItems = [
          { id: variantId, quantity: Math.min(quantity, maxQuantity) },
        ];
      }

      if (submitBtn) {
        submitBtn.disabled = true;
        submitBtn.textContent = "Adding...";
      }

      try {
        const cartUrl = window.routes.cart_add_url + ".js";

        const response = await fetch(cartUrl, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ items: payloadItems }),
        });

        const data = await response.json();

        if (response.ok) {
          submitBtn.textContent = "Added!";

          // Publish cart update event
          publish(PUB_SUB_EVENTS.cartUpdate, {
            action: "add",
            variant: data,
            product: this.currentProductData,
          });

          // Close modal after delay
          setTimeout(() => {
            this.close();
            submitBtn.textContent = originalText;
            submitBtn.disabled = false;
          }, 1000);
        } else {
          throw new Error(data.description || "Failed to add to cart");
        }
      } catch (error) {
        console.error("Error adding to cart:", error);

        // Publish error event
        publish(PUB_SUB_EVENTS.cartError, {
          error: error.message,
          context: "quick-add-modal",
          productHandle: this.currentProductHandle,
        });

        submitBtn.textContent = "Error - Try Again";
        setTimeout(() => {
          submitBtn.textContent = originalText;
          submitBtn.disabled = false;
        }, 2000);
      }
    });
  }
}

// Initialize everything when DOM is loaded
document.addEventListener("DOMContentLoaded", () => {
  // Initialize global instances
  window.quickAddModal = new QuickAddModal();

  // Event delegation for color swatches
  document.addEventListener("click", function (e) {
    // Color swatches
    const colorOption = e.target.closest(
      ".card-product__color-option:not(.disabled)"
    );
    if (colorOption) {
      e.preventDefault();
      if (colorOption.getAttribute("aria-current") === "true") return;

      const productCard = colorOption.closest(".card-product");
      const desktopImage = productCard.querySelector(
        ".card-product__image-link.hidden-mobile .card-product__image"
      );
      const mobileImage = productCard.querySelector(
        ".card-product__image-container.visible-mobile .card-product__image"
      );

      // Update active state
      colorOption
        .closest(".card-product__colors")
        .querySelectorAll(".card-product__color-option")
        .forEach((btn) => btn.setAttribute("aria-current", "false"));
      colorOption.setAttribute("aria-current", "true");

      // Update images
      const newImageSrc = colorOption.dataset.imageSrc;
      const newMobileImageSrc = colorOption.dataset.imageSrcMobile;
      const newAltText = colorOption.dataset.imageAlt;

      if (desktopImage && newImageSrc) desktopImage.src = newImageSrc;
      if (mobileImage && newMobileImageSrc) mobileImage.src = newMobileImageSrc;
      if (desktopImage) desktopImage.alt = newAltText;
      if (mobileImage) mobileImage.alt = newAltText;
    }
  });
});
